/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	BusinessProfileInterface,
	PropertyInterface,
	UserInterface,
} from "../../../../../redux-magic/sub-interfaces/sub-interfaces";

/* Component Imports */

import { LinearProgress, Typography, useTheme } from "@mui/material";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
import PublishButtonModal from "../../../../modals/PropertyModal/PublishUnpublishModal";

/* Library Function Imports */

import { useRouter } from "next/router";
import CalculatePropertyCompletionPercentage from "../../../../../lib/CalculatePropertyCompletionPercentage";

/* Styled Components */

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "100%",
	gap: "0.5rem",
	padding: "1rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	borderRadius: "16px",
	[theme.breakpoints.down("md")]: {
		display: "none",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	margin: "0rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const PublishPublicButtons = ({
	propertyData,
	dispatch,
	session,
	loading,
	profile_context,
	isEditor,
}: {
	propertyData: LocalPropertyInterface;
	dispatch: Function;
	session: any;
	loading: boolean;
	profile_context: StateInterface["profile_context"];
	isEditor: boolean;
}) => {
	const theme = useTheme();
	const router = React.useRef(useRouter()).current;

	const [publishmodal, setPublishmodal] = React.useState(false);

	const [updateButtonText, setUpdateButtonText] = React.useState<string>(propertyData.status);

	/* Button text */
	React.useEffect(() => {
		setUpdateButtonText(
			propertyData.status === "publish_requested"
				? router.locale === "ar-AE"
					? "تم طلب النشر"
					: router.locale === "kn-IN"
						? "ಪ್ರಕಟಿಸಲು ವಿನಂತಿಸಲಾಗಿದೆ"
						: "Publish requested"
				: propertyData.status === "published" && propertyData.published === true
					? router.locale === "ar-AE"
						? "إلغاء النشر"
						: router.locale === "kn-IN"
							? "ಅನ್‌ಪಬ್ಲಿಷ್"
							: "Unpublish"
					: propertyData.status === "working_in_progress"
						? router.locale === "ar-AE"
							? "العقار قيد المراجعة"
							: router.locale === "kn-IN"
								? "ಪ್ರಾಪರ್ಟಿ ಪರಿಶೀಲನೆಯಡಿ ಇದೆ."
								: "Property under review"
						: propertyData.status === "pending_qc"
							? router.locale === "ar-AE"
								? "العقار قيد المراجعة"
								: router.locale === "kn-IN"
									? "ಪ್ರಾಪರ್ಟಿ ಪರಿಶೀಲನೆಯಡಿ ಇದೆ."
									: "Property under review"
							: propertyData.status === "edited"
								? router.locale === "ar-AE"
									? "نشر"
									: router.locale === "kn-IN"
										? "ಪಬ್ಲಿಷ್"
										: "Publish"
								: router.locale === "ar-AE"
									? "نشر"
									: router.locale === "kn-IN"
										? "ಪಬ್ಲಿಷ್"
										: "Publish",
		);
		/* eslint-disable */
	}, [propertyData.status, propertyData.published]);

	if (loading) {
		return <MainContainer></MainContainer>;
	}
	return (
		<React.Fragment>
			<PublishButtonModal
				propertyData={propertyData as LocalPropertyInterface}
				open={publishmodal}
				handleClose={() => {
					setPublishmodal(false);
				}}
				dispatch={dispatch}
				profile_context={profile_context}
				isEditor={isEditor}
				session={session}
			/>

			<MainContainer>
				<Heading>
					{updateButtonText === "Unpublish" || updateButtonText === "إلغاء النشر" || updateButtonText === "ಅನ್‌ಪಬ್ಲಿಷ್"
						? router.locale === "ar-AE"
							? "إلغاء النشر"
							: router.locale === "kn-IN"
								? "ಅನ್‌ಪಬ್ಲಿಷ್"
								: "Unpublish"
						: router.locale === "ar-AE"
							? "النشر"
							: router.locale === "kn-IN"
								? "ಪಬ್ಲಿಷ್"
								: "Publish"}
					{router.locale === "ar-AE" ? " عقار" : router.locale === "kn-IN" ? " ಪ್ರಾಪರ್ಟಿ" : " Property"}
				</Heading>

				<BeegruButton
					id="spp_publish_button"
					flavor="primary"
					variant={
						updateButtonText === "Unpublish" || updateButtonText === "إلغاء النشر" || updateButtonText === "ಅನ್‌ಪಬ್ಲಿಷ್"
							? "outlined"
							: "contained"
					}
					sx={{ padding: "0rem", width: "100%" }}
					onClick={() => {
						setPublishmodal(true);
					}}
					disabled={
						(!propertyData.published &&
							CalculatePropertyCompletionPercentage(propertyData as PropertyInterface) < 100) ||
						propertyData.status === "publish_requested" ||
						propertyData.status === "working_in_progress" ||
						propertyData.status === "pending_qc"
					}
				>
					<Typography
						variant="body1"
						sx={{
							position: "absolute",
							width: "100%",
							height: "2.5rem",
							zIndex: 1,
							textAlign: "center",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color:
								updateButtonText === "Unpublish" ||
								updateButtonText === "إلغاء النشر" ||
								updateButtonText === "ಅನ್‌ಪಬ್ಲಿಷ್"
									? theme.palette.primary.main
									: "#FFFFFF",
						}}
					>
						{CalculatePropertyCompletionPercentage(propertyData as PropertyInterface) < 100 && !propertyData.published
							? CalculatePropertyCompletionPercentage(propertyData as PropertyInterface) + "%"
							: updateButtonText
								? updateButtonText
								: router.locale === "ar-AE"
									? "النشر"
									: router.locale === "kn-IN"
										? "ಪಬ್ಲಿಷ್"
										: "Publish"}
					</Typography>
					<LinearProgress
						variant="determinate"
						sx={{
							width: "100%",
							height: "2.5rem",
							borderRadius: "12px",
							"& .MuiLinearProgress-bar": {
								backgroundColor:
									updateButtonText === "Unpublish" ||
									updateButtonText === "إلغاء النشر" ||
									updateButtonText === "ಅನ್‌ಪಬ್ಲಿಷ್"
										? theme.palette.background.paper
										: theme.palette.primary.main,
							},
						}}
						value={
							!propertyData.published ? CalculatePropertyCompletionPercentage(propertyData as PropertyInterface) : 100
						}
					/>
				</BeegruButton>

				<Typography
					variant="caption"
					align="left"
					sx={{ color: "text.secondary" }}
				>
					{!propertyData.published
						? CalculatePropertyCompletionPercentage(propertyData as PropertyInterface) < 25
							? router.locale === "ar-AE"
								? "يرجى تعبئة الحقول الإلزامية"
								: router.locale === "kn-IN"
									? "ದಯವಿಟ್ಟು ಅಗತ್ಯ ಕ್ಷೇತ್ರಗಳನ್ನು ತುಂಬಿ."
									: "Please fill the mandatory fields"
							: CalculatePropertyCompletionPercentage(propertyData as PropertyInterface) < 75
								? router.locale === "ar-AE"
									? "باقي بعض الحقول فقط"
									: router.locale === "kn-IN"
										? "ಕೆಲವು ಕ್ಷೇತ್ರಗಳನ್ನು ಮಾತ್ರ ನಿರ್ವಹಿಸಬೇಕಿದೆ"
										: "Just a few more fields to go"
								: CalculatePropertyCompletionPercentage(propertyData as PropertyInterface) < 100
									? router.locale === "ar-AE"
										? "عقاركم جاهز تقريبًا للنشر"
										: router.locale === "kn-IN"
											? "ನಿಮ್ಮ ಆಸ್ತಿ ಪ್ರಕಟಿಸಲು ತತ್‌ಕ್ಷಣ ಸಿದ್ಧವಾಗಿದೆ."
											: "Your property is almost ready to publish"
									: router.locale === "ar-AE"
										? "نصيحة: العقارات التي تحتوي على تفاصيل أكثر تحصل على وصول أفضل"
										: router.locale === "kn-IN"
											? "ಸಲಹೆ: ಹೆಚ್ಚಿನ ವಿವರಗಳಿರುವ ಆಸ್ತಿಗಳಿಗೆ ಉತ್ತಮ ತಲುಪುವಿಕೆ ದೊರೆಯುತ್ತದೆ."
											: "Tip: Properties with more details get better reach"
						: router.locale === "ar-AE"
							? "تم نشر عقارك"
							: router.locale === "kn-IN"
								? "ನಿಮ್ಮ ಪ್ರಾಪರ್ಟಿ ಪಬ್ಲಿಷ್ ಮಾಡಲಾಗಿದೆ"
								: "Your property is published"}
				</Typography>
			</MainContainer>
		</React.Fragment>
	);
};

export default PublishPublicButtons;
